/**
 * useIncubatingBatches Hook
 *
 * Fetches production batches currently incubating (colonising).
 * Used by the Incubation operational dashboard.
 *
 * Includes batches in SHOOGLED or INCUBATING state, with calculated
 * expected end dates based on strain-specific incubation periods.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call production-operations Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callProductionOperations(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('production-operations', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch production batches in SHOOGLED or INCUBATING status
 * @returns {Object} { batches, summary, loading, error, refetch }
 */
export function useIncubatingBatches() {
  const [batches, setBatches] = useState([])
  const [summary, setSummary] = useState({ total_blocks: 0, total_spawn_bags: 0, ready_soon: 0, overdue: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('incubating_list')
      setBatches(response.data || [])
      setSummary(response.summary || { total_blocks: 0, total_spawn_bags: 0, ready_soon: 0, overdue: 0 })
    } catch (err) {
      console.error('[useIncubatingBatches] Error:', err)
      setError(err.message)
      setBatches([])
      setSummary({ total_blocks: 0, total_spawn_bags: 0, ready_soon: 0, overdue: 0 })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    batches,
    summary,
    loading,
    error,
    refetch: fetchBatches
  }
}

/**
 * Hook for incubation mutations
 * @returns {Object} { markAsColonised, loading, error }
 */
export function useIncubationMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Mark a production batch as fully colonised
   * @param {string} productionBatchId - Production batch UUID
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Updated batch
   */
  const markAsColonised = useCallback(async (productionBatchId, notes = null) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('mark_colonised', {
        production_batch_id: productionBatchId,
        notes
      })
      return response.data
    } catch (err) {
      console.error('[useIncubationMutations.markAsColonised] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    markAsColonised,
    loading,
    error
  }
}

export default useIncubatingBatches
